/**
 * API calls of the shop file's documents and email tabs, on the generated `InternalAdminApi`.
 *
 * Every call carries the shop's access grant (`shopGrant`); a refusal for want of one is
 * recognised by `isShopAccessRequired` in the caller, which then asks for a reason again.
 */

import axios from 'axios';
import {
  DownloadOpsShopDocumentPdfKindEnum,
  RegenerateOpsShopDocumentKindEnum,
  ResendOpsShopDocumentKindEnum,
} from '../../../../libs/api-client/src/generated/api/internal-admin-api';
import {
  OpsDocumentKind,
  type OpsDocumentActionResultDto,
  type OpsDocumentListDto,
  type OpsDocumentReissueDto,
  type OpsDocumentReissuePreviewDto,
  type OpsDocumentReissueResultDto,
  type OpsEmailFilterStatus,
  type OpsEmailLogDto,
  type OpsSmtpTestResultDto,
  type OpsSmtpTestStatusDto,
} from '../../../../libs/api-client/src/generated/models';
import { opsClient, shopGrant } from './ops-api';
import { errorCodeOf } from './ops-documents';

// Re-exported from the generated client rather than restated here — see `support-api.ts`.
export {
  OpsDocumentKind,
  OpsEmailFilterStatus,
} from '../../../../libs/api-client/src/generated/models';
export type {
  OpsDocumentActionResultDto as OpsDocumentActionResult,
  OpsDocumentListDto as OpsDocumentList,
  OpsDocumentReissuePreviewDto as OpsDocumentReissuePreview,
  OpsDocumentReissueResultDto as OpsDocumentReissueResult,
  OpsDocumentRowDto as OpsDocumentRow,
  OpsEmailLogDto as OpsEmailLog,
  OpsEmailLogRowDto as OpsEmailLogRow,
  OpsSmtpTestResultDto as OpsSmtpTestResult,
  OpsSmtpTestStatusDto as OpsSmtpTestStatus,
} from '../../../../libs/api-client/src/generated/models';

/*
 * The generator emits one path-parameter enum per operation, all with the same three values as
 * `OpsDocumentKind`. Mapped rather than cast, so a kind added on one side and not the other is a
 * compile error here instead of a 400 at runtime.
 */
const RESEND_KIND: Record<OpsDocumentKind, ResendOpsShopDocumentKindEnum> = {
  [OpsDocumentKind.Invoice]: ResendOpsShopDocumentKindEnum.Invoice,
  [OpsDocumentKind.CreditNote]: ResendOpsShopDocumentKindEnum.CreditNote,
  [OpsDocumentKind.Cancellation]: ResendOpsShopDocumentKindEnum.Cancellation,
};
const REGENERATE_KIND: Record<
  OpsDocumentKind,
  RegenerateOpsShopDocumentKindEnum
> = {
  [OpsDocumentKind.Invoice]: RegenerateOpsShopDocumentKindEnum.Invoice,
  [OpsDocumentKind.CreditNote]: RegenerateOpsShopDocumentKindEnum.CreditNote,
  [OpsDocumentKind.Cancellation]:
    RegenerateOpsShopDocumentKindEnum.Cancellation,
};
const DOWNLOAD_KIND: Record<
  OpsDocumentKind,
  DownloadOpsShopDocumentPdfKindEnum
> = {
  [OpsDocumentKind.Invoice]: DownloadOpsShopDocumentPdfKindEnum.Invoice,
  [OpsDocumentKind.CreditNote]: DownloadOpsShopDocumentPdfKindEnum.CreditNote,
  [OpsDocumentKind.Cancellation]:
    DownloadOpsShopDocumentPdfKindEnum.Cancellation,
};

/**
 * The refusal code of a failed call (`RECIPIENT_OVERRIDE_NEEDS_TICKET`, …), or null.
 *
 * @param error - Whatever the call threw.
 */
export function opsErrorCode(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  return errorCodeOf(error.response?.data);
}

// ── Documents ───────────────────────────────────────────────────────────────

/** Filters of the documents tab. */
export interface DocumentListFilters {
  readonly q?: string;
  readonly kind?: OpsDocumentKind;
  readonly page?: number;
}

/** One page of the shop's documents, or a search. */
export async function listDocuments(
  shopId: string,
  filters: DocumentListFilters,
): Promise<OpsDocumentListDto> {
  const q = filters.q?.trim();
  const { data } = await opsClient().listOpsShopDocuments({
    shopId,
    xOpsShopAccess: shopGrant(shopId),
    ...(q ? { q } : {}),
    ...(filters.kind ? { kind: filters.kind } : {}),
    ...(filters.page ? { page: filters.page } : {}),
  });
  return data;
}

/** Queues a resend of the archived PDF. */
export async function resendDocument(
  shopId: string,
  kind: OpsDocumentKind,
  documentId: string,
  body: { reason: string; recipientOverride?: string; ticketId?: string },
): Promise<OpsDocumentActionResultDto> {
  const { data } = await opsClient().resendOpsShopDocument({
    shopId,
    xOpsShopAccess: shopGrant(shopId),
    kind: RESEND_KIND[kind],
    documentId,
    opsDocumentResendDto: body,
  });
  return data;
}

/** Queues the regeneration of a MISSING PDF. */
export async function regenerateDocument(
  shopId: string,
  kind: OpsDocumentKind,
  documentId: string,
  reason: string,
): Promise<OpsDocumentActionResultDto> {
  const { data } = await opsClient().regenerateOpsShopDocument({
    shopId,
    xOpsShopAccess: shopGrant(shopId),
    kind: REGENERATE_KIND[kind],
    documentId,
    operatorReasonDto: { reason },
  });
  return data;
}

/** What a reissue of this invoice would draw, and whether it would be refused. Reads only. */
export async function previewReissue(
  shopId: string,
  documentId: string,
): Promise<OpsDocumentReissuePreviewDto> {
  const { data } = await opsClient().previewOpsShopInvoiceReissue({
    shopId,
    xOpsShopAccess: shopGrant(shopId),
    documentId,
  });
  return data;
}

/** Reissues an invoice: Storno plus a new invoice from the order's current data. */
export async function reissueInvoice(
  shopId: string,
  documentId: string,
  body: OpsDocumentReissueDto,
): Promise<OpsDocumentReissueResultDto> {
  const { data } = await opsClient().reissueOpsShopInvoice({
    shopId,
    xOpsShopAccess: shopGrant(shopId),
    documentId,
    opsDocumentReissueDto: body,
  });
  return data;
}

/**
 * The archived PDF as a Blob.
 *
 * Fetched through the authenticated client — the route needs the bearer token AND the grant, so
 * a plain link cannot open it. With `responseType: 'blob'` an error body arrives as a Blob too,
 * which would hide both the refusal code and the `SHOP_ACCESS_REQUIRED` the shop file prompts
 * on; it is parsed back into JSON on the error before rethrowing, so callers read it as usual.
 */
export async function downloadDocumentPdf(
  shopId: string,
  kind: OpsDocumentKind,
  documentId: string,
): Promise<Blob> {
  try {
    const response = await opsClient().downloadOpsShopDocumentPdf(
      {
        shopId,
        xOpsShopAccess: shopGrant(shopId),
        kind: DOWNLOAD_KIND[kind],
        documentId,
      },
      { responseType: 'blob' },
    );
    // Typed `void` by the generator (the route declares no JSON body); narrowed, not asserted.
    const body: unknown = response.data;
    if (!(body instanceof Blob)) {
      throw new Error('The PDF response carried no file');
    }
    return body;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await error.response.data.text());
      } catch {
        // Not JSON — leave the body as it came; the caller falls back to its generic copy.
      }
    }
    throw error;
  }
}

/**
 * Hands a Blob to the browser as a download.
 *
 * @param blob - The file.
 * @param fileName - The name to save it under.
 */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick: revoking synchronously can cancel the download in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

// ── Email ───────────────────────────────────────────────────────────────────

/** Filters of the email log. */
export interface EmailLogFilters {
  readonly recipient?: string;
  readonly status?: OpsEmailFilterStatus;
  readonly page?: number;
}

/** One page of the shop's email delivery log. */
export async function listEmailLog(
  shopId: string,
  filters: EmailLogFilters,
): Promise<OpsEmailLogDto> {
  const recipient = filters.recipient?.trim();
  const { data } = await opsClient().listOpsShopEmailLog({
    shopId,
    xOpsShopAccess: shopGrant(shopId),
    // The API wants at least two characters; a single one is still being typed.
    ...(recipient && recipient.length >= 2 ? { recipient } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.page ? { page: filters.page } : {}),
  });
  return data;
}

/** Queues an SMTP test to the signed-in operator's own address. */
export async function sendSmtpTest(
  shopId: string,
  reason: string,
): Promise<OpsSmtpTestResultDto> {
  const { data } = await opsClient().sendOpsShopSmtpTest({
    shopId,
    xOpsShopAccess: shopGrant(shopId),
    operatorReasonDto: { reason },
  });
  return data;
}

/** Polls a queued SMTP test. */
export async function fetchSmtpTestStatus(
  shopId: string,
  jobId: string,
): Promise<OpsSmtpTestStatusDto> {
  const { data } = await opsClient().getOpsShopSmtpTestStatus({
    shopId,
    xOpsShopAccess: shopGrant(shopId),
    jobId,
  });
  return data;
}
